import { useState, useEffect } from "react";
import { db } from "../../firebase_config";
import { doc, onSnapshot } from "firebase/firestore";

/**
 * Reads campaign config from `enrollment_desc/products`:
 *   - open_access_until: Timestamp — hard cutoff for the whole campaign
 *   - open_access_phases: [{ from: Timestamp, until?: Timestamp, prefixes: string[] }]
 *
 * A phase's prefixes are active while `phase.from <= now < phase.until`. If
 * `phase.until` is missing, the phase stays active for the rest of the campaign
 * (i.e. until `open_access_until`). Module access uses exact-match against
 * `activePrefixes` (no startsWith), so each entry must be a full module ID.
 */
export const useSiteWideOpenAccess = () => {
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [openAccess, setOpenAccess] = useState({
    isActive: false,
    activePrefixes: [],
    expiryByPrefix: {},
    until: null,
  });

  useEffect(() => {
    setError(null);
    setIsPending(true);

    const docRef = doc(db, "enrollment_desc", "products");
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const until = data.open_access_until?.toDate() || null;
          const phases = Array.isArray(data.open_access_phases)
            ? data.open_access_phases
            : [];
          const now = new Date();
          const withinWindow = until && now < until;

          // Map of full module ID → latest phase `until` (or campaign `until`
          // if the matching phase has no per-phase expiry). If a module appears
          // in multiple active phases, the latest expiry wins.
          const expiryByPrefix = {};
          if (withinWindow) {
            for (const p of phases) {
              const from = p?.from?.toDate ? p.from.toDate() : null;
              if (!from || from > now) continue;
              const phaseUntil = p?.until?.toDate ? p.until.toDate() : null;
              if (phaseUntil && now >= phaseUntil) continue;
              const effectiveUntil = phaseUntil || until;
              for (const prefix of Array.isArray(p.prefixes) ? p.prefixes : []) {
                const prev = expiryByPrefix[prefix];
                if (!prev || effectiveUntil > prev) {
                  expiryByPrefix[prefix] = effectiveUntil;
                }
              }
            }
          }
          const activePrefixes = Object.keys(expiryByPrefix);

          setOpenAccess({
            isActive: withinWindow && activePrefixes.length > 0,
            activePrefixes,
            expiryByPrefix,
            until,
          });
          setError(null);
          setIsPending(false);
        } else {
          setError("Open access configuration not found");
          setIsPending(false);
        }
      },
      (err) => {
        setError(err.message);
        setIsPending(false);
      }
    );
    return () => unsub();
  }, []);

  return { openAccess, error, isPending };
};
